import React, { useId } from 'react';
import type { TextureVariant } from '../../../data/textures';
type NoiseConfig = {
  freq: string;
  octaves: number;
  seed: number;
  discrete: boolean;
  rotate?: number;
  slope?: number;
};
const noise: Partial<Record<TextureVariant, NoiseConfig>> = {
  blotch: {
    freq: '0.13',
    octaves: 2,
    seed: 4,
    discrete: true
  },
  speckle: {
    freq: '0.85',
    octaves: 1,
    seed: 2,
    discrete: true
  },
  streaks: {
    freq: '0.9 0.05',
    octaves: 2,
    seed: 7,
    discrete: true
  },
  grain: {
    freq: '0.95',
    octaves: 2,
    seed: 9,
    discrete: false,
    slope: 2.4
  },
  wood: {
    freq: '0.7 0.025',
    octaves: 3,
    seed: 5,
    discrete: false,
    slope: 2.6
  },
  diagonal: {
    freq: '0.6 0.12',
    octaves: 2,
    seed: 3,
    discrete: false,
    rotate: 35,
    slope: 2.4
  },
  cells: {
    freq: '0.06',
    octaves: 1,
    seed: 11,
    discrete: false,
    slope: 4
  }
};
type TextureSwatchProps = {
  variant: TextureVariant;
};
export function TextureSwatch({
  variant
}: TextureSwatchProps) {
  const id = `tx${useId().replace(/:/g, '')}`;
  const cfg = noise[variant];
  if (cfg) {
    const slope = cfg.slope ?? 1;
    const intercept = -(slope - 1) / 2;
    return <svg viewBox="0 0 60 60" className="h-full w-full" aria-hidden="true" data-id="element-710" data-mp="19nqlip0p2fkv">
        <filter id={id} x="0" y="0" width="100%" height="100%" data-id="element-711" data-mp="19ptm22zh3r3u">
          <feTurbulence type="fractalNoise" baseFrequency={cfg.freq} numOctaves={cfg.octaves} seed={cfg.seed} data-id="element-712" data-mp="1not9567c7xxw" />
          <feColorMatrix type="saturate" values="0" data-id="element-713" data-mp="1imu65xfif31h" />
          <feComponentTransfer data-id="element-714" data-mp="1xkwcughu84my">
            {cfg.discrete ? <>
                <feFuncR type="discrete" tableValues="0 1" data-id="element-715" data-mp="1sml09y05rg11" />
                <feFuncG type="discrete" tableValues="0 1" data-id="element-716" data-mp="1sm2vue3cdvi1" />
                <feFuncB type="discrete" tableValues="0 1" data-id="element-717" data-mp="26dzl8admlraq" />
              </> : <>
                <feFuncR type="linear" slope={slope} intercept={intercept} data-id="element-718" data-mp="22bw5h9jxc0jn" />
                <feFuncG type="linear" slope={slope} intercept={intercept} data-id="element-719" data-mp="2gbnrnxklgn8q" />
                <feFuncB type="linear" slope={slope} intercept={intercept} data-id="element-720" data-mp="2g9t225ydo6yb" />
              </>}
            <feFuncA type="discrete" tableValues="1 1" data-id="element-721" data-mp="2bazromee0w2d" />
          </feComponentTransfer>
        </filter>
        <rect width="60" height="60" fill="#000" data-id="element-722" data-mp="18xlg82i6avri" />
        <g transform={cfg.rotate ? `rotate(${cfg.rotate} 30 30)` : undefined} data-id="element-723" data-mp="1d1t5c0sjmdq9">
          <rect x="-20" y="-20" width="100" height="100" filter={`url(#${id})`} data-id="element-724" data-mp="1hzumgg6es2z3" />
        </g>
      </svg>;
  }
  if (variant === 'bubbles') {
    return <svg viewBox="0 0 60 60" className="h-full w-full" aria-hidden="true" data-id="element-725" data-mp="1msw24qefw6jq">
        <defs data-id="element-726" data-mp="1rpe9zz52gl0a">
          <radialGradient id={`${id}g`} data-id="element-727" data-mp="1rs0chkd3oabn">
            <stop offset="0" stopColor="#fff" data-id="element-728" data-mp="1wohad6a3q31x" />
            <stop offset="0.75" stopColor="#555" data-id="element-729" data-mp="1wn60coq2bz2d" />
            <stop offset="1" stopColor="#000" data-id="element-730" data-mp="20q2iyfflrv6b" />
          </radialGradient>
          <pattern id={id} width="10" height="10" patternUnits="userSpaceOnUse" data-id="element-731" data-mp="25poxcs763hgp">
            <rect width="10" height="10" fill="#000" data-id="element-732" data-mp="25nkumdl08oi0" />
            <circle cx="5" cy="5" r="4.4" fill={`url(#${id}g)`} data-id="element-733" data-mp="6dmk3zp4zjbh" />
          </pattern>
        </defs>
        <rect width="60" height="60" fill={`url(#${id})`} data-id="element-734" data-mp="bcpdgmvr813m" />
      </svg>;
  }
  if (variant === 'scales') {
    return <svg viewBox="0 0 60 60" className="h-full w-full" aria-hidden="true" data-id="element-735" data-mp="3tpu2h8am9k9">
        <defs data-id="element-736" data-mp="3v5rjc9uuie1d">
          <pattern id={id} width="5" height="5" patternUnits="userSpaceOnUse" data-id="element-737" data-mp="3v5826vd0rtoe">
            <rect width="5" height="5" fill="#6d6d6d" data-id="element-738" data-mp="3q8reg29qfhbu" />
            <path d="M2.5 0 L5 2.5 L2.5 5 L0 2.5 Z" fill="#c9c9c9" stroke="#3a3a3a" strokeWidth="0.5" data-id="element-739" data-mp="3l8mvm5lcqaig" />
          </pattern>
        </defs>
        <rect width="60" height="60" fill={`url(#${id})`} data-id="element-740" data-mp="mr93vn2krw7q" />
      </svg>;
  }
  if (variant === 'gradient') {
    return <svg viewBox="0 0 60 60" className="h-full w-full" aria-hidden="true" data-id="element-741" data-mp="hsft42eoajb9">
        <defs data-id="element-742" data-mp="crs3nl63eaj5">
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1" data-id="element-743" data-mp="crieu0wsa9f1">
            <stop offset="0" stopColor="#fff" data-id="element-744" data-mp="8rrep6ldagxu" />
            <stop offset="1" stopColor="#000" data-id="element-745" data-mp="8qyfcqoqmtpq" />
          </linearGradient>
        </defs>
        <rect width="60" height="60" fill={`url(#${id})`} data-id="element-746" data-mp="2xkltbeernqbh" />
      </svg>;
  }
  return <svg viewBox="0 0 60 60" className="h-full w-full" aria-hidden="true" data-id="element-747" data-mp="2tgns8y5vs5fs">
      <defs data-id="element-748" data-mp="l890hbvmm99w">
        <pattern id={id} width="15" height="15" patternUnits="userSpaceOnUse" data-id="element-749" data-mp="l7pqchc7wp6f">
          <rect width="15" height="15" fill="#000" data-id="element-750" data-mp="pzzdxqpv1d5j" />
          <rect x="0.8" y="1.6" width="6" height="4" rx="1" fill="#eee" data-id="element-751" data-mp="q0sdo7qej2e6" />
          <rect x="9.2" y="0.6" width="4" height="6" rx="1" fill="#eee" data-id="element-752" data-mp="bbw4268p5hbg" />
          <rect x="1.6" y="8.2" width="4" height="6" rx="1" fill="#eee" data-id="element-753" data-mp="g94m86tuhlcp" />
          <rect x="8.2" y="9.4" width="6" height="4" rx="1" fill="#eee" data-id="element-754" data-mp="13z8s6kui9x6v" />
        </pattern>
      </defs>
      <rect width="60" height="60" fill={`url(#${id})`} data-id="element-755" data-mp="18ybjz3lh66ww" />
    </svg>;
}